import { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  IconButton,
  TextField,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HelpIcon from '@mui/icons-material/Help';
import SubjectIcon from '@mui/icons-material/Subject';
import QuizIcon from '@mui/icons-material/Quiz';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/apiClient';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { FaqItem, CreateFaqPayload, UpdateFaqPayload } from '../types';
import { parseErrorMessage } from '../utils/errorUtils';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormDialog from '../components/FormDialog';
import { faqSchema } from '../schemas/faq.schema';
import CustomButton from '../components/CustomButton';

interface FaqFormState {
  id?: string;
  question: string;
  answer: string;
  category: string;
  isActive: boolean;
}

export default function FaqPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const { t } = useTranslation();

  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FaqFormState | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deleteFaqTarget, setDeleteFaqTarget] = useState<FaqItem | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);

  const canManageFaqs = user?.role === 'master_full' || user?.role === 'dealer_admin';

  const loadFaqs = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (categoryFilter) params.category = categoryFilter;
      if (!canManageFaqs) params.activeOnly = 'true';

      const [faqData, catData] = await Promise.all([
        api.listFaqs(null, params),
        api.getFaqCategories(null),
      ]);
      setFaqs(faqData);
      setCategories(catData);
    } catch (err) {
      showError(parseErrorMessage(err, t('faq.errors.load')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFaqs();
  }, [categoryFilter]);

  const handleOpenDialog = (faq?: FaqItem) => {
    if (faq) {
      setForm({
        id: faq.id,
        question: faq.question,
        answer: faq.answer,
        category: faq.category || '',
        isActive: faq.isActive,
      });
    } else {
      setForm({
        question: '',
        answer: '',
        category: '',
        isActive: true,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setBusy(true);
    try {
      if (form.id) {
        await api.updateFaq(null, form.id, {
          question: form.question.trim(),
          answer: form.answer.trim(),
          category: form.category.trim() || null,
          isActive: form.isActive,
        });
        showSuccess(t('faq.messages.updated'));
      } else {
        await api.createFaq(null, {
          question: form.question.trim(),
          answer: form.answer.trim(),
          category: form.category.trim() || null,
        });
        showSuccess(t('faq.messages.created'));
      }
      setForm(null);
      loadFaqs();
    } catch (err) {
      showError(parseErrorMessage(err, t('faq.errors.save')));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteFaqTarget) return;
    setBusy(true);
    try {
      await api.deleteFaq(null, deleteFaqTarget.id);
      showSuccess(t('faq.messages.deleted', 'FAQ excluído com sucesso.'));
      await loadFaqs();
    } catch (err) {
      showError(parseErrorMessage(err, t('faq.errors.delete')));
    } finally {
      setBusy(false);
      setDeleteFaqTarget(null);
    }
  };

  const handleAccordionChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce(
    (acc, faq) => {
      const cat = faq.category || t('faq.uncategorized', 'Sem categoria');
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(faq);
      return acc;
    },
    {} as Record<string, FaqItem[]>,
  );

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {t('faq.title', 'Perguntas Frequentes')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('faq.subtitle', 'Encontre respostas para dúvidas comuns')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {categories.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="faq-category-label">{t('faq.category', 'Categoria')}</InputLabel>
              <Select
                labelId="faq-category-label"
                label={t('faq.category', 'Categoria')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">{t('common.all', 'Todas')}</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {canManageFaqs && (
            <CustomButton
              variant="primary"
              icon={<AddIcon />}
              iconPosition="left"
              onClick={() => handleOpenDialog()}
            >
              {t('faq.new', 'Nova Pergunta')}
            </CustomButton>
          )}
          <Tooltip title={t('common.refresh')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<RefreshIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={loadFaqs}
                disabled={loading}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* FAQ List */}
      {loading ? (
        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={60} />
          ))}
        </Stack>
      ) : faqs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <HelpOutlineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary">
            {t('faq.empty', 'Nenhuma pergunta frequente encontrada')}
          </Typography>
        </Paper>
      ) : (
        Object.entries(groupedFaqs).map(([category, items]) => (
          <Box key={category}>
            {!categoryFilter && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 1 }}>
                {category}
              </Typography>
            )}
            {items.map((faq) => (
              <Accordion
                key={faq.id}
                expanded={expanded === faq.id}
                onChange={handleAccordionChange(faq.id)}
                variant="outlined"
                sx={{
                  mb: 1,
                  opacity: faq.isActive ? 1 : 0.6,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: '100%', pr: 2 }}
                  >
                    <Typography sx={{ flexGrow: 1 }}>{faq.question}</Typography>
                    {faq.isGlobal && (
                      <Chip label={t('faq.global', 'Global')} size="small" color="info" />
                    )}
                    {!faq.isActive && (
                      <Chip label={t('faq.inactive', 'Inativo')} size="small" color="default" />
                    )}
                    {canManageFaqs && (
                      <Stack direction="row" spacing={0}>
                        <Tooltip title={t('common.edit')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(faq);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('faq.actions.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteFaqTarget(faq);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))
      )}

      {/* Create/Edit Modal */}
      <FormDialog
        open={Boolean(form)}
        onClose={() => {
          if (busy) return;
          setForm(null);
        }}
        formId="faq-form"
        title={form?.id ? t('faq.dialogEditTitle') : t('faq.dialogTitle')}
        subtitle={
          form?.id
            ? t('faq.dialogEditSubtitle', 'Editar pergunta frequente')
            : t('faq.dialogSubtitle', 'Nova pergunta frequente')
        }
        icon={form?.id ? <EditIcon /> : <QuizIcon />}
        maxWidth="md"
        loading={busy}
        submitText={
          form?.id
            ? t('faq.dialogSubmitEdit', 'Salvar Alterações')
            : t('faq.dialogSubmit', 'Criar FAQ')
        }
        disableSubmit={form ? !faqSchema.safeParse(form).success : true}
      >
        <Stack spacing={3} component="form" id="faq-form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('faq.form.question')}
                value={form?.question ?? ''}
                onChange={(event) =>
                  setForm((prev) => (prev ? { ...prev, question: event.target.value } : prev))
                }
                required
                disabled={busy}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HelpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('faq.form.answer')}
                value={form?.answer ?? ''}
                multiline
                minRows={4}
                onChange={(event) =>
                  setForm((prev) => (prev ? { ...prev, answer: event.target.value } : prev))
                }
                required
                disabled={busy}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                      <SubjectIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('faq.form.category')}
                value={form?.category ?? ''}
                onChange={(event) =>
                  setForm((prev) => (prev ? { ...prev, category: event.target.value } : prev))
                }
                disabled={busy}
                placeholder={t('faq.form.categoryPlaceholder', 'Ex: Licenças, Pagamentos...')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiTypography-root': { ml: 1 },
                }}
                control={
                  <Switch
                    checked={form?.isActive ?? true}
                    onChange={(e) =>
                      setForm((prev) => (prev ? { ...prev, isActive: e.target.checked } : prev))
                    }
                    disabled={busy}
                  />
                }
                label={t('faq.form.active', 'Ativo')}
              />
            </Grid>
          </Grid>
        </Stack>
      </FormDialog>

      <ConfirmationDialog
        open={Boolean(deleteFaqTarget)}
        title={t('common.confirmDelete')}
        content={t('faq.confirmDelete', {
          question: deleteFaqTarget?.question,
        })}
        onClose={() => setDeleteFaqTarget(null)}
        onConfirm={handleConfirmDelete}
        severity="error"
        confirmText={t('common.delete')}
        loading={busy}
      />
    </Stack>
  );
}
